import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import i18n from '../i18n';

export default function Devices() {
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    axios.get('/api/v1/devices')
      .then(res => setDevices(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/v1/devices');
      setDevices(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearSorts = () => {
    setSearch('');
  };

  const filteredDevices = devices.filter(d =>
    d.device_name.toLowerCase().includes(search.toLowerCase()) ||
    d.gsrn?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center py-40">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#155e75]" />
    </div>
  );

  const mapSource = (code) => {
    const map = {
      'SOLAR': 'Solar PV',
      'WIND': 'Wind Offshore',
      'BIOMASS': 'Biomass',
      'F01010101': 'Solar Fuel',
      'T010000': 'Solar Energy',
      'T020000': 'Wind Energy',
      'T030000': 'Hydro Energy',
    };
    return map[code] || code;
  };

  const mapTech = (code) => {
    const map = {
      'T010000': 'Solar Photovoltaic',
      'T020000': 'Wind Power',
      'T030000': 'Hydro Electric',
      'T030100': 'Hydro Run-of-river',
      'F01010101': 'Photovoltaic',
    };
    return map[code] || code;
  };

  return (
    <div className="space-y-6 animate-fade-in px-2 sm:px-4 md:px-0">
      

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
          {i18n.t('production_plants')}
        </h1>
        
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 justify-between sm:justify-end">
          <div className="flex items-center gap-2">
            <button
              onClick={handleClearSorts}
              className="text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-[#155e75] transition-colors"
            >
              {i18n.t('clear_sorts')}
            </button>
            <span className="text-slate-200">|</span>
            <button
              onClick={handleRefresh}
              className="text-[10px] text-slate-500 font-bold uppercase tracking-widest hover:text-[#155e75] transition-colors"
            >
              {i18n.t('refresh_grid')}
            </button>
          </div>
          
          <button
            onClick={() => navigate('/devices/register')}
            className="w-full sm:w-auto bg-[#155e75] hover:bg-[#0e4e61] text-white text-xs font-bold uppercase tracking-wider px-5 py-3 rounded-xl shadow-md shadow-[#155e75]/10 transition-all text-center"
          >
            {i18n.t('register_device')}
          </button>
        </div>
      </div>

      <div className="bg-white border border-[#adc6ce]/40 rounded-2xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3 bg-slate-50/50">
          <div className="relative w-full sm:w-72">
            <MagnifyingGlassIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder={i18n.t('search_plants')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-xs border border-slate-200 rounded-xl outline-none focus:border-[#155e75] focus:ring-1 focus:ring-[#155e75] bg-white"
            />
          </div>
          <div className="flex items-center justify-end gap-2">
            <ListBulletIcon className="w-5 h-5 text-[#155e75]" />
            <Squares2X2Icon className="w-5 h-5 text-slate-300" />
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="ent-table w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100 bg-slate-50/30">
                <th className="w-8 px-4 text-center py-4">
                  <input type="checkbox" className="rounded border-slate-300 text-[#155e75] focus:ring-[#155e75]" />
                </th>
                <th className="cursor-pointer py-4">{i18n.t('dev_name')} <span className="text-[10px] text-slate-400">↑↓</span></th>
                <th className="cursor-pointer py-4">GSRN <span className="text-[10px] text-slate-400">↑↓</span></th>
                <th className="cursor-pointer py-4">{i18n.t('status_col')} <span className="text-[10px] text-slate-400">↑↓</span></th>
                <th className="text-center py-4">{i18n.t('dev_locked')}</th>
                <th className="py-4">{i18n.t('dev_energy_source')}</th>
                <th className="py-4">{i18n.t('dev_technology')}</th>
                <th className="py-4">{i18n.t('dev_grid_ref')}</th>
                <th className="py-4">{i18n.t('dev_owner')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredDevices.map((device) => (
                <tr
                  key={device.id}
                  className="hover:bg-blue-50/20 transition-colors cursor-pointer"
                  onClick={() => navigate(`/devices/${device.id}`)}
                >
                  <td className="px-4 text-center py-4" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="rounded border-slate-300 text-[#155e75] focus:ring-[#155e75]" />
                  </td>
                  <td className="font-semibold text-blue-600 hover:underline py-4">
                    {device.device_name}
                  </td>
                  <td className="font-mono text-slate-500 text-xs py-4">{device.gsrn || 'N/A'}</td>
                  <td className="py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold ${device.status === 'QUALIFIED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                      {device.status === 'QUALIFIED' ? i18n.t('dev_active') : device.status}
                    </span>
                  </td>
                  <td className="text-center py-4">
                    <LockClosedIcon className="w-4 h-4 text-slate-300 mx-auto" />
                  </td>
                  <td className="text-[10px] uppercase font-black text-slate-700 py-4">{mapSource(device.energy_source_code)}</td>
                  <td className="text-[10px] uppercase font-black text-[#155e75] py-4">{mapTech(device.technology_code)}</td>
                  <td className="text-slate-500 text-[10px] uppercase font-black py-4">{device.grid_reference || i18n.t('national_grid')}</td>
                  <td className="text-slate-700 text-[10px] uppercase font-black truncate max-w-[180px] py-4" title={device.owner_id}>{device.owner_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>


        <div className="block md:hidden divide-y divide-slate-100">
          {filteredDevices.map((device) => (
            <div
              key={device.id}
              onClick={() => navigate(`/devices/${device.id}`)}
              className="p-5 hover:bg-slate-50/50 active:bg-slate-50 transition-colors cursor-pointer space-y-4"
            >
              <div className="flex justify-between items-start gap-2">
                <span className="font-bold text-[#155e75] text-sm hover:underline">
                  {device.device_name}
                </span>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[9px] font-bold ${device.status === 'QUALIFIED' ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-amber-50 text-amber-700 border border-amber-100'}`}>
                  {device.status === 'QUALIFIED' ? i18n.t('dev_active') : device.status}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-y-3 gap-x-2 text-[11px]">
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">GSRN</span>
                  <span className="font-mono text-slate-600">{device.gsrn || 'N/A'}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Owner</span>
                  <span className="text-slate-700 font-semibold truncate block max-w-[140px]">{device.owner_name}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Source</span>
                  <span className="text-slate-700 font-bold">{mapSource(device.energy_source_code)}</span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[9px] uppercase font-bold tracking-wider">Technology</span>
                  <span className="text-[#155e75] font-bold">{mapTech(device.technology_code)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>


        {filteredDevices.length === 0 && (
          <div className="py-20 text-center text-slate-400 text-xs italic">
            {i18n.t('no_devices')}
          </div>
        )}

        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row gap-4 items-center justify-between text-xs text-slate-500">
          <div>
            1 - {filteredDevices.length} {i18n.t('of')} {filteredDevices.length} {i18n.t('items')}
          </div>
          
          <div className="flex items-center gap-4">
            <span>{i18n.t('items_per_page')}: 20</span>
            <div className="flex gap-1">
              <button className="px-2.5 py-1.5 border border-slate-200 rounded-lg bg-white disabled:opacity-50" disabled>«</button>
              <button className="px-3 py-1.5 border border-slate-200 rounded-lg bg-white font-bold text-[#155e75]">1</button>
              <button className="px-2.5 py-1.5 border border-slate-200 rounded-lg bg-white disabled:opacity-50" disabled>»</button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}